import classNames from 'classnames';
import { type CSSProperties, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChartThemeOverrideContext } from '@revelio/replots';

import type { CapturedChartImage } from '../../Chart/chart-menu/data-download/ppt-export/ppt-chart-image';
import type {
  ChartImageRenderer,
  ChartImageRequest,
} from '../../Chart/chart-menu/data-download/ppt-export/ppt-flow-report-exporter';
import {
  PageExportProvider,
  usePageExport,
} from '../../Chart/chart-menu/PageExportProvider';
import { TabVisibilityProvider } from '../../TabVisibilityContext';
import {
  createPrintFrame,
  type PrintFrame,
  settle,
  waitForImages,
} from '../print/print-frame';
import printStyles from '../print/ReportFlowPrint.module.css';
import flowStyles from '../ReportFlow.module.css';
import { ReportFlowBlockView } from '../ReportFlowBlockView';
import {
  captureChartBox,
  chartSvgs,
  embeddedFontCss,
  usedFontFamilies,
} from './capture-chart-box';
import styles from './ReportFlowPpt.module.css';

// Renders only the charts PowerPoint has no native form for (plus image items) at slide size.
const CAPTURE_SCALE = 2;
const CHART_SETTLE_MS = 300;

type RenderSession = {
  frame: PrintFrame;
  requests: ChartImageRequest[];
  onCaptured: (images: Map<string, CapturedChartImage>) => void;
};

const PptRenderPhase = ({ frame, requests, onCaptured }: RenderSession) => {
  const { waitForPageDataReady } = usePageExport();

  useEffect(() => {
    let cancelled = false;
    const capture = async () => {
      await settle();
      await waitForPageDataReady({ quiet: true });
      await frame.syncStyles();
      await waitForImages(frame.document);
      await new Promise((resolve) => setTimeout(resolve, CHART_SETTLE_MS));
      await settle();
      if (cancelled) return;

      const boxes = Array.from(
        frame.document.querySelectorAll<HTMLElement>('[data-ppt-item-id]')
      );
      const fontCss = await embeddedFontCss(
        document,
        usedFontFamilies(boxes.flatMap(chartSvgs))
      );
      const images = new Map<string, CapturedChartImage>();
      for (const box of boxes) {
        const itemId = box.dataset.pptItemId;
        if (!itemId || cancelled) continue;
        try {
          const image = await captureChartBox(box, {
            scale: CAPTURE_SCALE,
            fontCss,
          });
          if (image) images.set(itemId, image);
        } catch (error) {
          console.warn(`Chart capture for PPT failed (${itemId}):`, error);
        }
      }
      if (!cancelled) onCaptured(images);
    };
    void capture().catch((error: unknown) => {
      console.warn('Chart render for PPT failed:', error);
      if (!cancelled) onCaptured(new Map());
    });
    return () => {
      cancelled = true;
    };
  }, [frame, onCaptured, waitForPageDataReady]);

  return (
    <div className={classNames(styles.renderRoot, flowStyles.printScope)}>
      {requests.map((request) => (
        <div
          key={request.itemId}
          className={styles.box}
          data-ppt-item-id={request.itemId}
          style={
            {
              '--ppt-box-w': `${request.widthPx}px`,
              '--ppt-box-h': `${request.heightPx}px`,
            } as CSSProperties
          }
        >
          <ReportFlowBlockView
            block={{
              ...request.leaf,
              heightPx: request.heightPx,
              widthPct: undefined,
              align: undefined,
              startsPage: undefined,
            }}
          />
        </div>
      ))}
    </div>
  );
};

export const ReportFlowPptHost = () => {
  const { setFlowReportChartRenderer } = usePageExport();
  const [session, setSession] = useState<RenderSession | null>(null);

  useEffect(() => {
    let unmounted = false;
    let active: RenderSession | null = null;
    let queue: Promise<unknown> = Promise.resolve();
    const noImages = () => new Map<string, CapturedChartImage>();

    const renderOnce = async (requests: ChartImageRequest[]) => {
      if (unmounted) return noImages();
      const frame = await createPrintFrame({
        frameClassName: styles.frame,
        bodyClassName: printStyles.printBody,
      });
      try {
        await frame.syncStyles();
        if (unmounted) return noImages();
        return await new Promise<Map<string, CapturedChartImage>>(
          (onCaptured) => {
            active = { frame, requests, onCaptured };
            setSession(active);
          }
        );
      } finally {
        active = null;
        setSession(null);
        frame.dispose();
      }
    };
    // One mount slot: a second export queues behind the first instead of replacing it mid-capture.
    const render: ChartImageRenderer = (requests) => {
      const run = queue.then(() => renderOnce(requests));
      queue = run.catch(() => undefined);
      return run;
    };
    setFlowReportChartRenderer(render);
    return () => {
      unmounted = true;
      setFlowReportChartRenderer(null);
      // The frame is gone, so settle the in-flight capture empty rather than hang the export.
      active?.onCaptured(noImages());
    };
  }, [setFlowReportChartRenderer]);

  if (!session) return null;

  return createPortal(
    <ChartThemeOverrideContext.Provider value="light">
      <PageExportProvider>
        <TabVisibilityProvider isVisible>
          <PptRenderPhase {...session} />
        </TabVisibilityProvider>
      </PageExportProvider>
    </ChartThemeOverrideContext.Provider>,
    session.frame.mountNode
  );
};
